export default function SpeedConvenienceSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Why Selling Land the Traditional Way Takes Forever
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            If you've ever tried to sell vacant land through a realtor, you know the frustration. Here's why land sits on the MLS for 6-12 months (or longer) without selling.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="w-full h-96 rounded-lg overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/5f3cd2eb35b0e7059310d4b77c13b269.jpg"
              alt="Vacant land sitting unsold on the market"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold mb-6 text-gray-900">The Traditional Sale Problem</h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              Most landowners don't realize how difficult it is to sell vacant land through traditional methods. Unlike homes that sell in 30-60 days, land can sit on the market for 6-18 months without a single offer.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              The challenges compound when you factor in limited buyer pools, financing difficulties, and the ongoing costs of property taxes and maintenance while you wait for a buyer.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-lg mb-6">
              <i className="ri-time-line text-red-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Limited Buyer Pool</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Most homebuyers are looking for houses, not land. The pool of potential buyers for vacant land is significantly smaller, which means your property can sit on the market for months without a single serious inquiry. Banks are also hesitant to finance land purchases, further limiting your buyer pool to cash buyers only.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Real estate agents often prioritize house listings because they close faster and generate higher commissions. Your land listing may get minimal marketing attention, buried beneath dozens of home listings.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-lg mb-6">
              <i className="ri-money-dollar-circle-line text-orange-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Financing Challenges</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Unlike homes, vacant land is difficult to finance. Most conventional lenders won't offer mortgages for raw land, and those that do require 30-50% down payments with higher interest rates. This eliminates 80% of potential buyers who need financing.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Even if a buyer is interested, the loan approval process for land can take 60-90 days, and many deals fall through when financing is denied. This creates uncertainty and delays that can stretch for months.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-yellow-100 rounded-lg mb-6">
              <i className="ri-tools-line text-yellow-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">The "As-Is" Problem</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Does your land have overgrown vegetation, trash, old structures, or access issues? Traditional buyers want "turnkey" properties. If your land needs clearing, grading, or has any issues, most buyers will walk away or demand steep discounts after inspections.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Title issues, easement disputes, zoning restrictions, or boundary problems can kill deals instantly. Realtors often refuse to list properties with these complications because they're too difficult to sell.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
              <i className="ri-hand-coin-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Hidden Costs Add Up</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              While your land sits on the market, you're still paying property taxes, liability insurance, and possibly HOA fees. If you're paying a realtor's listing fee or monthly marketing costs, those expenses compound over time. A 6% commission on a $50,000 land sale is $3,000 - money that comes directly out of your pocket at closing.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Survey costs, title work, and closing fees can add another $1,500-$3,000 to your expenses. By the time you sell traditionally, you may net far less than expected.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-12 text-white text-center">
          <h3 className="text-3xl font-bold mb-4">There's a Better Way: Sell Your Land for Cash</h3>
          <p className="text-xl mb-8 max-w-3xl mx-auto leading-relaxed">
            When you sell to TRex RE LLC, you avoid all these problems. We buy land in any condition, handle all paperwork, pay all closing costs, and close on your timeline. No waiting, no uncertainty, no hassles.
          </p>
          <a href="tel:+1-915-206-5814" className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-teal-600 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-phone-fill"></i>
            <span>Get Your Cash Offer Today</span>
          </a>
        </div>
      </div>
    </section>
  );
}